import { Component, OnDestroy, OnInit, Optional, ViewChild} from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ReportService } from '../../_services';
import { ColumnDef, DEFAULT_PAGE_SIZE, PageChange, RowAction, SortChange, SortDirection, StatusVariant } from '../../shared/data-table/data-table.model';
import { readRememberedPageSize } from '../../shared/data-table/page-size-storage';
import { SuccessModalHelperService } from '../../_helpers/success-modal-helper/success-modal-helper.service';
import { ReportDataService } from '../report-data.service';

import { DeliveryStatusFilter, ReportSentDataRequest } from './../../_models/reports/report-sent-data-request.model';
import { ReportSentData } from './../../_models/reports/report-sent-data.model';
import { PagedResponse } from './../../_models/common/paged-response.model';
import { ThemeService } from '../../_helpers/theming-engine/theme.service';
import { ErrorModalHelperService } from '../../_helpers/error-modal-helper/error-modal-helper.service';
import { ReportType } from '../../_models/reports/report-type.model';
import { ReportCoordinatorFilter } from '../../_models/reports/report-coordinator-filter.model';
import { ReportTutorFilter } from '../../_models/reports/report-tutor-filter.model';
import { ReportStudentFilter } from '../../_models/reports/report-student-filter.model';
import { ReportClient } from '../../_models/reports/report-client.model';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { MatOption } from '@angular/material/core';
import { FeatureSwitchService } from '../../_services/feature-switch/feature-switch.service';
import { FeatureSwitchKeys } from '../../_constants/feature-switch-keys';
import { FilterSpec, FilterState } from '../../shared/filter-ux/filter-state';
import { ScreenFilterUx } from '../../shared/filter-ux/screen-filter-ux';
import { DatePreset, isDatePreset, parseIsoDate, resolveDatePreset, toIsoDate } from '../../shared/filter-ux/date-presets';
import { DatePresetSelection } from '../../shared/filter-ux/date-presets.component';
import { LeapV2EvidenceService } from '../../tutor-reports/tutor-report-flows/tutor-report-leap-v2/services/leap-v2-evidence.service';
import { ReportFilterLookupsService } from '../report-filter-lookups.service';

/** How long the History filters settle before the list reloads; the Reports tab uses the same. */
export const REFILTER_DEBOUNCE_MS = 400;

@Component({
    selector: 'reports-sent',
    templateUrl: 'reports-sent.component.html',
    styleUrls: ['reports-sent.component.css'],
    standalone: false
})

export class ReportsSentComponent implements OnInit,OnDestroy {

  @ViewChild('allSelected', { static: true }) private allSelected: MatOption;

  public reportRows: ReportSentData[] = [];
  public columns: ColumnDef<ReportSentData>[] = [];
  public selectedAllreports: boolean = false;
  public frmReportSent: UntypedFormGroup;
  /** True once a search has been run; gates showing the table (matches the pre-paging behaviour). */
  public hasSearched: boolean = false;
  /** Total rows matching the current filters, across all pages — drives the table's "of Z" footer. */
  public totalCount: number = 0;
  public loading: boolean = false;
  public pageNumber: number = 1;
  public pageSize: number = DEFAULT_PAGE_SIZE;
  public sortField: string = null;
  public sortDir: SortDirection = 'asc';
  /** Key under which the table remembers the user's page-size choice for the session. */
  public readonly tableId: string = 'reports-sent';
  public errorMsg: string;
  public clientDetails: any = [];
  public exportSelected: string = '';
  public selectedReportUrl: string;
  public reportSelected: boolean = false;
  /**
   * The filters.reports-sent switch. Off, the list is exactly as it was before the filter work: no
   * brand or recipient filters, and none of their request parameters.
   */
  public readonly filtersKey = FeatureSwitchKeys.filtersReportsSent;
  public filtersEnabled: boolean = false;

  /** Selected brands; empty means every brand the user may see. */
  public companyIds: number[] = [];
  /** Recipient search: the referrer email contains this text. */
  public recipient: string = '';
  /**
   * search.autocomplete.reports-sent: the Recipient box suggests the recipient emails on the report lines loaded so
   * far (the referrer list onClientSelected loads has names, not emails). Off, it is a plain box.
   */
  public autocompleteEnabled: boolean = false;
  public recipientEmails: string[] = [];
  /**
   * Delivery filter; null ("Any") sends nothing, so the API's default list applies — which hides
   * failed lines. Picking Failed is the only way to see those.
   */
  public deliveryStatus: DeliveryStatusFilter | null = null;
  public readonly deliveryOptions: { value: DeliveryStatusFilter | null, label: string }[] = [
    { value: null, label: 'Any' },
    { value: 'sent', label: 'Sent' },
    { value: 'failed', label: 'Failed' },
    { value: 'notSent', label: 'Not sent' },
    { value: 'optedOut', label: 'Opted out' }
  ];
  /** The date preset behind the start and end dates, or null when they were set by hand. */
  public datePreset: DatePreset | null = null;

  /**
   * Query-string names for the filters, which are also what is remembered. Ids, codes, presets and dates
   * only: free text, names and emails stay out of the URL (browser history, telemetry) and browser storage.
   */
  private static readonly filterSpec: FilterSpec = {
    from: 'string', to: 'string', preset: 'string', report: 'string', client: 'number', tutor: 'number',
    student: 'number', brand: 'number[]'
  };
  /** Remembered filters and filter state in the URL; used only while filters.reports-sent is on. */
  private filterUx: ScreenFilterUx;
  /** Filters to restore once both the switch and the report types have arrived. */
  private pendingFilterState: FilterState = null;
  /** Search requests are funnelled through this subject and switchMap'd so a rapid page/sort click
   * cannot have an earlier, slower response land after a later one (last-write-wins). */
  private searchRequested: Subject<ReportSentDataRequest> = new Subject<ReportSentDataRequest>();
  /** A filter changed; with filters.reports-sent on, the list reloads once they settle, as on the Reports tab. */
  private readonly filterChange = new Subject<void>();

  public filteredReports: Array<ReportType> = new Array<ReportType>();
  public filteredCoordinatorList: Array<ReportCoordinatorFilter> = new Array<ReportCoordinatorFilter>();
  public filteredTutorList: Array<ReportTutorFilter> = new Array<ReportTutorFilter>();
  public filteredStudentList: Array<ReportStudentFilter> = new Array<ReportStudentFilter>();
  public filteredClientList: Array<ReportClient> = new Array<ReportClient>();

  public studentTooltip: string

  private unsubscribe: Subject<void> = new Subject<void>();
  
  /** Loads the filter lists for the chosen client; ReportLookupNoteComponent shows a failed one. */
  private filterLookups: ReportFilterLookupsService;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private reportService: ReportService,
    public reportDataService: ReportDataService,
    public themeService: ThemeService,
    private successModalHelper: SuccessModalHelperService,
    private errorModalHelper: ErrorModalHelperService,
    private featureSwitches: FeatureSwitchService,
    private evidenceService: LeapV2EvidenceService,
    @Optional() private router: Router = null,
    @Optional() private route: ActivatedRoute = null,
    @Optional() filterLookups?: ReportFilterLookupsService
  ) {
    this.filterLookups = filterLookups ?? new ReportFilterLookupsService(reportService, reportDataService);
  }

  ngOnInit() {
    this.filterUx = new ScreenFilterUx(this.filtersKey, ReportsSentComponent.filterSpec, this.router, this.route);

    // Read before the first fetch so the request and the table footer agree on page size.
    this.pageSize = readRememberedPageSize(this.tableId) ?? DEFAULT_PAGE_SIZE;

    let defaultEndDate = new Date();
    let defaultStartDate = new Date(defaultEndDate.getTime() - (30*24*60*60*1000));

    
    this.frmReportSent = this.formBuilder.group({
      startDate: [defaultStartDate, [Validators.required]],
      endDate: [defaultEndDate, [Validators.required]],
      coordinators: [null],
      clients: [null],
      tutors: [null],
      students: [null],
      reports : [null]
    });

    this.errorMsg = '';

    this.buildColumns();

    this.reportDataService.reportListUpdated().pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      this.filteredReports = this.reportDataService.reports;
      this.applyPendingFilterState();
    });
    this.reportDataService.clientListUpdated().pipe(takeUntil(this.unsubscribe)).subscribe(() => this.filteredClientList = this.reportDataService.clientList);

    this.searchRequested.pipe(
      switchMap((params: ReportSentDataRequest) => {
        this.loading = true;
        return this.reportService.getGeneratedReports(params);
      }),
      takeUntil(this.unsubscribe)
    ).subscribe({
      next: (response: PagedResponse<ReportSentData>) => {
        this.hasSearched = true;
        this.totalCount = response.totalCount;
        this.reportRows = response.items;
        this.rememberRecipients(response.items);
        this.loading = false;
      },
      error: error => {
        this.loading = false;
        this.errorModalHelper.show("An error has occurred, please try again", error);
        console.error(error);
      }
    });

    this.frmReportSent.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(() => this.filtersChanged());
    this.filterChange.pipe(debounceTime(REFILTER_DEBOUNCE_MS), takeUntil(this.unsubscribe)).subscribe(() => this.filterReports());

    // After the form and the search pipeline exist: a restored filter may run a search straight away.
    this.featureSwitches.isEnabled(FeatureSwitchKeys.searchAutocompleteReportsSent).pipe(takeUntil(this.unsubscribe))
      .subscribe(enabled => this.autocompleteEnabled = enabled);
    this.featureSwitches.isEnabled(this.filtersKey).pipe(takeUntil(this.unsubscribe)).subscribe(enabled => {
      this.filtersEnabled = enabled;
      this.buildColumns();
      // A shared link's filters, else this user's last ones. Read only once the switch is known to be on.
      this.pendingFilterState = enabled ? this.filterUx.initialState() : null;
      this.applyPendingFilterState();
    });

    setTimeout(() => {
      this.studentTooltip = "Please select a learner";
    }, 100);
  }

  ngOnDestroy(): void {
    this.releaseReport();
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  /** The table's columns; the Delivery column exists only while filters.reports-sent is on. */
  private buildColumns(): void {
    const deliveryColumn: ColumnDef<ReportSentData>[] = this.filtersEnabled
      ? [{ field: 'deliveryStatus', header: 'Delivery', type: 'template' }]
      : [];
    this.columns = [
      { field: 'generatedDate', header: 'Generated Date', type: 'date', sortable: true },
      { field: 'clientName', header: 'Client name', sortable: true },
      { field: 'studentName', header: 'Learner name', sortable: true },
      { field: 'reportType', header: 'Report Type', sortable: true },
      ...deliveryColumn
    ];
  }

  /** Display label for a line's delivery status; rows from before delivery status existed show a dash. */
  public deliveryLabel(row: ReportSentData): string {
    return DELIVERY_LABELS[row?.deliveryStatus] ?? '—';
  }

  /** Pill colour: green delivered, red failed, amber not sent, grey opted out or unknown. */
  public deliveryVariant(row: ReportSentData): StatusVariant {
    switch (row?.deliveryStatus) {
      case 'Sent': return 'green';
      case 'Failed': return 'red';
      case 'NotSent': return 'amber';
      default: return 'gray';
    }
  }

  /** Hover text for the Delivery cell: when it was delivered, or why it was not. */
  public deliveryDetail(row: ReportSentData): string {
    if (row?.deliveryStatus === 'Sent' && row.deliveredUtc) {
      return 'Delivered ' + new Date(row.deliveredUtc).toLocaleString('en-GB');
    }
    if (row?.deliveryStatus === 'OptedOut') {
      return 'The referrer has opted out of automatic report emails.';
    }
    return row?.failureReason ?? '';
  }

  /** The list follows the filters with filters.reports-sent on; off, the Filter button runs it. */
  public filtersChanged(): void {
    if (this.filtersEnabled) {
      this.filterChange.next();
    }
  }

  public filterReports(): void {
    this.reportSelected = false;
    this.selectedReportUrl = "";
    if (this.frmReportSent.invalid) {
      return;
    }

    // A new filter always starts back at page 1 - staying on, say, page 7 of the previous
    // filter's results would silently show the wrong rows for the new one.
    this.pageNumber = 1;
    if (this.filtersEnabled) {
      this.filterUx.save(this.filterState());
    }
    this.runSearch();
  }

  /**
   * Shows the list for filters chosen elsewhere: the combined Reports page's "See all in History" (report type by name,
   * one client, a preset or dates). Restored as a remembered or linked filter would be, so the list loads when the
   * report types have.
   */
  public showFilters(state: FilterState): void {
    this.pendingFilterState = state;
    this.applyPendingFilterState();
  }
  /** Clear all: back to the screen's defaults, forgotten, and out of the URL. The table is left as it is. */
  public clearAll(): void {
    const defaultEndDate = new Date();
    this.frmReportSent.reset({
      startDate: new Date(defaultEndDate.getTime() - (30*24*60*60*1000)),
      endDate: defaultEndDate
    });
    this.companyIds = [];
    this.recipient = '';
    this.datePreset = null;
    this.filterUx.clear();
  }

  public onDatePreset(selection: DatePresetSelection): void {
    this.datePreset = selection.preset;
    this.frmReportSent.patchValue({ startDate: parseIsoDate(selection.from), endDate: parseIsoDate(selection.to) });
  }

  /** A preset is remembered as the preset, so it resolves afresh on the next visit. */
  private filterState(): FilterState {
    const value = this.frmReportSent.value;
    return {
      preset: this.datePreset,
      from: this.datePreset || !value.startDate ? null : toIsoDate(new Date(value.startDate)),
      to: this.datePreset || !value.endDate ? null : toIsoDate(new Date(value.endDate)),
      report: value.reports?.name ?? null,
      client: value.clients,
      tutor: value.tutors,
      student: value.students,
      brand: this.companyIds
    };
  }

  /**
   * Restores remembered or linked filters. The report type is an object from the report list, so this
   * waits for the list. With a report type restored the list is loaded, as it would be after Filter.
   */
  private applyPendingFilterState(): void {
    const state = this.pendingFilterState;
    if (!state || !this.frmReportSent || (this.reportDataService.reports ?? []).length === 0) {
      return;
    }
    this.pendingFilterState = null;

    this.datePreset = isDatePreset(state.preset) ? state.preset : null;
    const range = this.datePreset ? resolveDatePreset(this.datePreset) : null;
    const from = parseIsoDate(range ? range.from : state.from as string);
    const to = parseIsoDate(range ? range.to : state.to as string);
    const report = this.reportDataService.reports.find(r => r.name === state.report) ?? null;
    this.frmReportSent.patchValue({
      startDate: from ?? this.frmReportSent.value.startDate,
      endDate: to ?? this.frmReportSent.value.endDate,
      reports: report,
      clients: (state.client as number) ?? null,
      tutors: (state.tutor as number) ?? null,
      students: (state.student as number) ?? null
    });
    if (state.client != null) {
      this.onClientSelected(state.client);
    }
    this.companyIds = (state.brand as number[]) ?? [];

    if (report) {
      this.filterReports();
    }
  }

  /** Builds the request for the current form values plus the current page/sort state. */
  private buildSearchParams(): ReportSentDataRequest {
    let params: ReportSentDataRequest = new ReportSentDataRequest();
    params.startDate = new Date(this.setDate(this.frmReportSent.value.startDate));
    params.endDate = new Date(this.setDate(this.frmReportSent.value.endDate));

    params.reportType = this.frmReportSent.value.reports?.name ?? null;

    var clientId = this.frmReportSent.value.clients;
    // The Client Reports role alone decides what History shows: a referral-only link sees the whole client.
    if (clientId != null) {
      params.clientId = clientId.toString();
    }
    var studentId = this.frmReportSent.value.students;
    if (studentId != null) {
      params.studentId = studentId;
    }
    var tutorId = this.frmReportSent.value.tutors;
    if (tutorId != null) {
      params.tutorId = tutorId;
    }

    // The switched filters travel only when filters.reports-sent is on.
    if (this.filtersEnabled) {
      params.companyIds = this.companyIds;
      params.referrerContains = this.recipient?.trim() || null;
      if (this.deliveryStatus) {
        params.deliveryStatus = this.deliveryStatus;
      }
    }

    params.pageNumber = this.pageNumber;
    params.pageSize = this.pageSize;
    params.sortField = this.sortField;
    params.sortDirection = this.sortDir;

    return params;
  }

  /** Issues the search for the current form/page/sort state via the switchMap'd request pipeline. */
  private runSearch(): void {
    this.searchRequested.next(this.buildSearchParams());
  }

  /** A page click or a page-size change from the table - re-issues the search for the new page. */
  public onPageChange(event: PageChange): void {
    this.pageNumber = event.page;
    this.pageSize = event.pageSize;
    this.runSearch();
  }

  /**
   * A column header sort. Only records the new sort state here - the table also emits `pageChange`
   * (reset to page 1) for server-side sorting, and that handler is what re-issues the request, so a
   * sort click results in exactly one request carrying both the sort and the page reset.
   */
  public onSortChange(event: SortChange): void {
    this.sortField = event.field;
    this.sortDir = event.direction;
  }

  public onRowAction(event: RowAction<ReportSentData>): void {
    if (event.action === 'view') {
      this.viewReport(event.row);
    }
  }

  public viewReport(item: ReportSentData): void {
    this.releaseReport();
    // A Failed line (listed only when the delivery filter asks for it) has no report to show.
    if (!item.reportURI) {
      this.reportSelected = false;
      return;
    }
    // reportURI is the API's link (api/Files/Report/{id}), never a storage link (bug 40444). It needs the
    // signed-in user's headers, so the file is fetched through the API: a PDF is shown here, anything else
    // (a spreadsheet) is saved.
    const name = ` - `;
    this.evidenceService.download({ name, url: item.reportURI }).subscribe(blob => {
      if (blob?.type === 'application/pdf') {
        this.selectedReportUrl = URL.createObjectURL(blob);
        this.reportSelected = true;
      } else {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = name;
        link.click();
        URL.revokeObjectURL(url);
      }
    });
  }

  private releaseReport(): void {
    if (this.selectedReportUrl) {
      URL.revokeObjectURL(this.selectedReportUrl);
    }
    this.selectedReportUrl = "";
    this.reportSelected = false;
  }

  public clearMoreOptions(): void {
    this.frmReportSent.controls['coordinators'].reset();
    this.frmReportSent.controls['clients'].reset();
    this.frmReportSent.controls['tutors'].reset();
    this.frmReportSent.controls['students'].reset();
  }

  private setDate(date): number {
    var d = new Date(date);
    return d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  }

  /** Adds the distinct recipient emails on these lines to the Recipient box's suggestions. */
  private rememberRecipients(rows: ReportSentData[]): void {
    const emails = new Set(this.recipientEmails);
    (rows ?? []).forEach(row => {
      const email = (row?.referrerEmail ?? '').trim();
      if (email) {
        emails.add(email);
      }
    });
    this.recipientEmails = Array.from(emails);
  }

  public onClientSelected(value: any): void {
    // A lookup that fails leaves its list empty with an inline Retry note, never an error popup (40760).
    this.filterLookups.load(value);
  }

}

/** Labels for the stored ClientGeneratedReports.DeliveryStatus values. */
const DELIVERY_LABELS: { [status: string]: string } = {
  Sent: 'Sent',
  Failed: 'Failed',
  NotSent: 'Not sent',
  OptedOut: 'Opted out'
};

export class ReportsData {
  reports: any[];
  selectedAllreports: boolean = false;
}
